import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let _client: SupabaseClient<Database> | null = null;

function getClient(): SupabaseClient<Database> {
  if (!_client) {
    if (!url || !anonKey) {
      throw new Error(
        'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Set them in Cloudflare → Settings → Build → Variables and Secrets, then redeploy.'
      );
    }
    _client = createClient<Database>(url, anonKey, {
      auth: { persistSession: true, autoRefreshToken: true },
    });
  }
  return _client;
}

/** Lightweight check that the client can reach the project. */
export async function pingSupabase(): Promise<{ ok: boolean; message: string }> {
  if (!url || !anonKey) {
    return {
      ok: false,
      message:
        'Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY at build time. Add them under Cloudflare → Settings → Build → Variables and Secrets, then redeploy.',
    };
  }
  try {
    // auth.getSession() succeeds whether or not the user is signed in,
    // and proves the client can reach the Supabase API.
    const { error } = await getClient().auth.getSession();
    if (error) return { ok: false, message: error.message };
    return { ok: true, message: 'Connected to Supabase.' };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Unknown error' };
  }
}

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_, prop) {
    return Reflect.get(getClient(), prop);
  },
});
